import { buildReportSectionKey, parsePretextSectionHeading } from './reportPretext';

const ARABIC_SCRIPT_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
const LATIN_LETTER_RE = /[A-Za-zÀ-ÖØ-öø-ÿ]/g;

const _LABEL_LINE_RE = /^\[([^\]]+)\]\s*$/;

/**
 * Parse ``[Label]\ncontent`` blocks from bilingual text.
 * Returns an array of ``{label, content}`` objects, or null if no labels found.
 */
export function parseLabeledBilingualBlocks(text) {
  if (!text || typeof text !== 'string') return null;
  const lines = text.trim().split('\n');
  const blocks = [];
  let currentLabel = null;
  let currentLines = [];

  const flush = () => {
    if (currentLabel !== null) {
      blocks.push({ label: currentLabel, content: currentLines.join('\n').trim() });
    }
  };

  for (const line of lines) {
    const match = _LABEL_LINE_RE.exec(line.trim());
    if (match) {
      flush();
      currentLabel = match[1].trim();
      currentLines = [];
    } else if (currentLabel === null) {
      return null;
    } else {
      currentLines.push(line);
    }
  }
  flush();
  return blocks.length > 0 ? blocks : null;
}

/** True when text contains at least one ``[Label]``-only line. */
export function textHasLabeledBilingualBlocks(text) {
  if (!text || typeof text !== 'string') return false;
  return text.trim().split('\n').some((l) => _LABEL_LINE_RE.test(l.trim()));
}

const NON_LATIN_TARGETS = new Set(['ar', 'he', 'zh', 'ja', 'ko', 'ru', 'hi', 'ur']);

export function normalizeReportText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function reportTextsAreEquivalent(left, right) {
  return normalizeReportText(left) === normalizeReportText(right);
}

export function latinCharacterRatio(text) {
  const sample = String(text || '');
  const latin = (sample.match(LATIN_LETTER_RE) || []).length;
  const arabic = (sample.match(ARABIC_SCRIPT_RE) || []).length;
  const total = latin + arabic;
  return total > 0 ? latin / total : 0;
}

export function isPredominantlyArabic(text, threshold = 0.3) {
  const sample = String(text || '');
  const arabic = (sample.match(ARABIC_SCRIPT_RE) || []).length;
  const latin = (sample.match(LATIN_LETTER_RE) || []).length;
  const total = arabic + latin;
  return total > 0 && arabic / total >= threshold;
}

export function isPredominantlyLatin(text, threshold = 0.8) {
  return latinCharacterRatio(text) >= threshold;
}

export function translationAppearsSuccessful(baseline, translated, targetLanguage) {
  if (!translated) return false;

  const execBase = baseline?.executive_summary || '';
  const execTrans = translated?.executive_summary || '';
  if (reportTextsAreEquivalent(execBase, execTrans)) {
    return false;
  }

  const target = String(targetLanguage || '').trim().toLowerCase();
  if (!target || target === 'auto' || target === 'en') {
    return true;
  }

  if (target === 'ar' || target === 'ur') {
    return isPredominantlyArabic(execTrans);
  }

  if (NON_LATIN_TARGETS.has(target)) {
    return !isPredominantlyLatin(execTrans);
  }

  return true;
}

export function indexBaselineSectionsByKey(baselineSections = []) {
  const keyed = new Map();
  (baselineSections || []).forEach((section, index) => {
    if (!section) return;
    keyed.set(buildReportSectionKey(section, index), section);
  });
  return keyed;
}

export function findBaselineSectionForClient(clientSection, clientIndex, baselineSections = []) {
  if (!baselineSections?.length) return {};
  if (clientSection?.pretext_id) {
    const byId = baselineSections.find(
      (section) => section?.pretext_id === clientSection.pretext_id,
    );
    if (byId) return byId;
  }
  const clientParsed = parsePretextSectionHeading(clientSection?.heading);
  if (clientParsed?.index != null) {
    const byScenarioIndex = baselineSections.find((section) => {
      const parsed = parsePretextSectionHeading(section?.heading);
      return parsed?.index === clientParsed.index;
    });
    if (byScenarioIndex) return byScenarioIndex;
  }
  const keyed = indexBaselineSectionsByKey(baselineSections);
  const key = buildReportSectionKey(clientSection || {}, clientIndex);
  if (keyed.has(key)) return keyed.get(key);
  return baselineSections[clientIndex] || {};
}

export function resolveBilingualMetadataColumns(baselineValue, clientValue) {
  const baseline = String(baselineValue || '').trim();
  const client = String(clientValue || '').trim();

  let english = '';
  let arabic = '';

  for (const text of [baseline, client]) {
    if (!text) continue;
    if (isPredominantlyArabic(text)) {
      if (!arabic) arabic = text;
    } else if (isPredominantlyLatin(text)) {
      if (!english) english = text;
    } else if (!english) {
      english = text;
    }
  }

  if (!english && baseline && isPredominantlyLatin(baseline)) {
    english = baseline;
  }
  if (!english && client && isPredominantlyLatin(client)) {
    english = client;
  }
  if (!arabic && client && isPredominantlyArabic(client)) {
    arabic = client;
  }
  if (!arabic && baseline && isPredominantlyArabic(baseline) && !reportTextsAreEquivalent(baseline, english)) {
    arabic = baseline;
  }

  if (english && arabic && reportTextsAreEquivalent(english, arabic)) {
    arabic = client && isPredominantlyArabic(client) ? client : '';
  }

  if (english && isPredominantlyArabic(english) && !isPredominantlyLatin(english)) {
    english = '';
  }

  return { english, arabic };
}

export function resolveBilingualSectionColumns(baselineSection = {}, clientSection = {}) {
  const heading = resolveBilingualMetadataColumns(
    baselineSection.heading,
    clientSection.heading,
  );
  const body = resolveBilingualMetadataColumns(
    baselineSection.body,
    clientSection.body,
  );
  const bulletCount = Math.max(
    baselineSection.bullets?.length || 0,
    clientSection.bullets?.length || 0,
  );
  const bullets = [];
  for (let index = 0; index < bulletCount; index += 1) {
    bullets.push(
      resolveBilingualMetadataColumns(
        baselineSection.bullets?.[index],
        clientSection.bullets?.[index],
      ),
    );
  }
  return { heading, body, bullets };
}

export function reportsDifferForTranslation(baseline, translated) {
  if (!baseline || !translated) return false;

  if (!reportTextsAreEquivalent(baseline.executive_summary, translated.executive_summary)) {
    return true;
  }

  const baseSections = baseline.sections || [];
  const transSections = translated.sections || [];
  for (let index = 0; index < baseSections.length; index += 1) {
    if (!reportTextsAreEquivalent(baseSections[index]?.body, transSections[index]?.body)) {
      return true;
    }
  }

  return false;
}

export function validateReportTranslation(baseline, translated, targetLanguage) {
  const warnings = [];
  const target = String(targetLanguage || '').trim().toLowerCase();

  if (!target || target === 'auto' || target === 'en') {
    return { ok: true, warnings };
  }

  if (!reportsDifferForTranslation(baseline, translated)) {
    warnings.push(
      `Report content appears unchanged after translation to ${targetLanguage}.`,
    );
  } else if (!translationAppearsSuccessful(baseline, translated, targetLanguage)) {
    warnings.push(
      'Translated report may still be in English. Review side-by-side before export.',
    );
  }

  return { ok: warnings.length === 0, warnings };
}
